import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Sparkles, Star, CheckCircle2, Crown, Award, Shield, Heart, Zap, X } from 'lucide-react';

// Custom Celey Balloon Icon - matches the brand
const CeleyBalloon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Balloon body */}
    <path d="M12 2C8.13 2 5 5.13 5 9c0 3.17 2.11 5.85 5 6.71V17c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-1.29c2.89-.86 5-3.54 5-6.71 0-3.87-3.13-7-7-7z" />
    {/* Balloon knot */}
    <path d="M11 18h2v1h-2z" />
    {/* Balloon string */}
    <path d="M12 19c-.28 0-.5.22-.5.5v2c0 .28.22.5.5.5s.5-.22.5-.5v-2c0-.28-.22-.5-.5-.5z" />
    {/* Highlight reflection */}
    <ellipse cx="9" cy="7" rx="1.5" ry="2" fill="white" opacity="0.3" />
  </svg>
);

// Filled Star Icon for trust signal
const FilledStar = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// Map icon names to Lucide icons
const iconMap = {
  'sparkles': Sparkles,
  'star': Star,
  'filled-star': FilledStar,
  'balloon': CeleyBalloon,
  'check-circle': CheckCircle2,
  'crown': Crown,
  'award': Award,
  'shield': Shield,
  'heart': Heart,
  'zap': Zap
};

// Map color names to Tailwind classes
const colorMap = {
  'amber': {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: 'text-amber-500',
    style: 'outline'
  },
  // TRUST SIGNAL: Founding Provider - Neutral background with green icon
  'founding': {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-800',
    icon: 'text-emerald-600',
    style: 'outline',
    shadow: ''
  },
  'gold': {
    bg: 'bg-gradient-to-r from-amber-500 to-yellow-500',
    border: 'border-amber-600',
    text: 'text-white',
    icon: 'text-amber-100',
    style: 'solid',
    shadow: 'shadow-sm'
  },
  'yellow': {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    icon: 'text-yellow-500',
    style: 'outline'
  },
  'emerald': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    icon: 'text-emerald-500',
    style: 'outline'
  },
  'blue': {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    icon: 'text-blue-500',
    style: 'outline'
  },
  'purple': {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    icon: 'text-purple-500',
    style: 'outline'
  },
  'rose': {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-700',
    icon: 'text-rose-500',
    style: 'outline'
  }
};

// Badge tooltip content - customize per badge type
const badgeTooltipContent = {
  'Founding Provider': {
    title: 'Founding Provider',
    description: 'This provider joined Celey early and helped shape the platform.'
  },
  'Top Rated': {
    title: 'Top Rated',
    description: 'Consistently receives excellent reviews from customers.'
  },
  'Verified': {
    title: 'Verified',
    description: 'Identity and business information has been verified by Celey.'
  }
};

/**
 * BadgeDisplay - Displays a single badge with icon and clickable tooltip
 * @param {Object} badge - Badge object with label, icon, color, description
 * @param {string} size - 'sm', 'md', or 'lg'
 * @param {boolean} clickable - Whether the badge opens a tooltip on click
 */
export const BadgeDisplay = ({ badge, size = 'sm', clickable = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!badge) return null;
  
  const IconComponent = iconMap[badge.icon] || Sparkles;
  const colors = colorMap[badge.color] || colorMap.emerald;
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-1 gap-1.5',
    lg: 'text-base px-3 py-1.5 gap-2'
  };
  
  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  // Get tooltip content - use custom content if available, otherwise use badge description
  const tooltipContent = badgeTooltipContent[badge.label] || {
    title: badge.label,
    description: badge.description || 'A special designation on Celey.'
  };

  const badgeElement = (
    <Badge
      variant={colors.style === 'solid' ? 'default' : 'outline'}
      className={`
        ${sizeClasses[size]}
        ${colors.bg} ${colors.border} ${colors.text}
        ${colors.shadow || ''}
        font-semibold inline-flex items-center
        ${clickable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
        transition-all
      `}
    >
      <IconComponent className={`${iconSizes[size]} ${colors.icon}`} />
      {badge.label}
    </Badge>
  );

  // If not clickable, just return the badge
  if (!clickable) {
    return badgeElement;
  }

  // Clickable badge with popover tooltip
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {badgeElement}
      </PopoverTrigger>
      <PopoverContent 
        className="w-64 p-0 shadow-lg" 
        align="start"
        sideOffset={8}
      >
        <div className="relative">
          {/* Close button - subtle, top-right corner */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
          
          {/* Tooltip content */}
          <div className="p-3 pr-8">
            <div className="flex items-center gap-2 mb-1">
              <IconComponent className={`h-4 w-4 ${colors.icon}`} />
              <h4 className="font-semibold text-sm text-gray-900">
                {tooltipContent.title}
              </h4>
            </div>
            <p className="text-sm text-gray-600 leading-snug">
              {tooltipContent.description}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * BadgeList - Displays multiple badges in a row
 * @param {Array} badges - Array of badge objects
 * @param {string} size - 'sm', 'md', or 'lg'
 * @param {number} maxVisible - Maximum number of badges to show
 * @param {boolean} clickable - Whether badges are clickable
 */
export const BadgeList = ({ badges, size = 'sm', maxVisible = 3, className = '', clickable = true }) => {
  if (!badges || badges.length === 0) return null;
  
  const visibleBadges = badges.slice(0, maxVisible);
  const remainingCount = badges.length - maxVisible;
  
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {visibleBadges.map((badge) => (
        <BadgeDisplay key={badge.id} badge={badge} size={size} clickable={clickable} />
      ))}
      {remainingCount > 0 && (
        <Badge variant="outline" className="text-xs px-2 py-0.5 bg-gray-50 border-gray-200 text-gray-500">
          +{remainingCount} more
        </Badge>
      )}
    </div>
  );
};

export default BadgeDisplay;
